import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { ApiError } from '@/api/client';
import {
  fetchDocuments,
  fetchDocumentByUrl,
  fetchSimilarDocuments
} from './api';
import {
  DocumentListResponse,
  DocumentDetail,
  DocumentListParams,
  SimilarDocument,
  SimilarDocumentsParams
} from './types';

export const documentKeys = {
  all: ['documents'] as const,
  lists: () => [...documentKeys.all, 'list'] as const,
  list: (params?: DocumentListParams) =>
    [...documentKeys.lists(), params] as const,
  details: () => [...documentKeys.all, 'detail'] as const,
  detail: (sourceUrl: string) =>
    [...documentKeys.details(), sourceUrl] as const,
  similar: (documentId: string, params?: SimilarDocumentsParams) =>
    [...documentKeys.all, 'similar', documentId, params] as const
};

/**
 * Hook to fetch list of documents
 */
export function useDocuments(
  params?: DocumentListParams,
  options?: Omit<
    UseQueryOptions<DocumentListResponse, ApiError>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: documentKeys.list(params),
    queryFn: () => fetchDocuments(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options
  });
}

/**
 * Hook to fetch single document with full details
 */
export function useDocument(
  sourceUrl: string | null | undefined,
  options?: Omit<
    UseQueryOptions<DocumentDetail, ApiError>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: documentKeys.detail(sourceUrl!),
    queryFn: () => fetchDocumentByUrl(sourceUrl!),
    enabled: !!sourceUrl,
    staleTime: 10 * 60 * 1000, // 10 minutes
    ...options
  });
}

/**
 * Hook to fetch similar documents
 */
export function useSimilarDocuments(
  documentId: string | null | undefined,
  params?: SimilarDocumentsParams,
  options?: Omit<
    UseQueryOptions<SimilarDocument[], ApiError>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: documentKeys.similar(documentId!, params),
    queryFn: () => fetchSimilarDocuments(documentId!, params),
    enabled: !!documentId,
    staleTime: 10 * 60 * 1000, // 10 minutes
    ...options
  });
}
